import api from "./axios";

export interface Semester {
    id: string;
    academic_year_id: string;
    name: string;
    code: string; // Backend-generated business identifier (read-only)
    start_date: string;
    end_date: string;
    is_active: boolean;
    academic_year?: {
        id: string;
        name: string;
    };
    created_at?: string;
    updated_at?: string;
}

export interface SemesterFormData {
    academic_year_id: string;
    name: string;
    start_date: string;
    end_date: string;
    is_active?: boolean;
}

export interface SemesterListResponse {
    success: boolean;
    message: string;
    data: {
        items: Semester[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    };
}

export interface SemesterResponse {
    success: boolean;
    message: string;
    data: Semester;
}

const semesterApi = {
    // Get all semesters with filters
    getSemesters: async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
        is_active?: boolean;
        academic_year_id?: string;
    }): Promise<SemesterListResponse> => {
        const response = await api.get("/v1/semesters", { params });
        return response.data;
    },

    // Get semester by ID
    getSemester: async (id: string): Promise<SemesterResponse> => {
        const response = await api.get(`/v1/semesters/${id}`);
        return response.data;
    },

    // Create new semester
    createSemester: async (
        data: SemesterFormData,
    ): Promise<SemesterResponse> => {
        const response = await api.post("/v1/semesters", data);
        return response.data;
    },

    // Update semester
    updateSemester: async (
        id: string,
        data: SemesterFormData,
    ): Promise<SemesterResponse> => {
        const response = await api.put(`/v1/semesters/${id}`, data);
        return response.data;
    },

    // Delete semester
    deleteSemester: async (id: string): Promise<{ success: boolean }> => {
        const response = await api.delete(`/v1/semesters/${id}`);
        return response.data;
    },

    // Set semester as active
    setActiveSemester: async (id: string): Promise<SemesterResponse> => {
        const response = await api.post(`/v1/semesters/${id}/set-active`);
        return response.data;
    },

    // Get active semester
    getActiveSemester: async (): Promise<SemesterResponse> => {
        const response = await api.get("/v1/semesters/active");
        return response.data;
    },

    // Get semesters by academic year
    getSemestersByAcademicYear: async (
        academicYearId: string,
    ): Promise<{ success: boolean; message: string; data: Semester[] }> => {
        const response = await api.get(
            `/v1/academic-years/${academicYearId}/semesters`,
        );
        return response.data;
    },
};

export default semesterApi;
